<script setup>
import { computed, useSlots, ref, watch } from 'vue' // ❌ Removed defineExpose
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'

const props = defineProps({
  okDisabled: { type: Boolean, default: false },
  modelValue: { type: Boolean, default: false },
  title: { type: String, default: '' },
  description: { type: String, default: 'This is a modal' },
  okTitle: { type: String, default: null },
  cancelTitle: { type: String, default: null },
  okVariant: { type: String, default: 'default' },
  cancelVariant: { type: String, default: 'outline' },
  hideHeader: { type: Boolean, default: false },
  hideFooter: { type: Boolean, default: false },
  size: { type: String, default: 'md' },
  noCloseOnBackdrop: { type: Boolean, default: false },
  noCloseOnEsc: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue', 'ok', 'cancel', 'show', 'hide'])
const slots = useSlots()

// --- FIX: Use a local Ref for state, synced with Props ---
const localOpen = ref(props.modelValue)

// 1. If Prop changes (parent uses v-model), update local
watch(() => props.modelValue, (val) => {
  localOpen.value = val
})

// 2. If Local changes (we call .open()), emit to parent
watch(localOpen, (val) => {
  emit('update:modelValue', val)
  emit(val ? 'show' : 'hide')
})

const maxWidthClass = computed(() => {
  switch (props.size) {
  case 'sm': return 'sm:max-w-[425px]'
  case 'lg': return 'sm:max-w-[800px]'
  case 'xl': return 'sm:max-w-[1140px]'
  case 'full': return 'sm:max-w-[100vw] h-screen'
  case 'md':
  default: return 'sm:max-w-[550px]'
  }
})

// --- METHODS ---

const handleOpenChange = (val) => {
  if (!val) localOpen.value = val
}

const handleOk = () => {
  let preventClose = false
  const bvEvent = {
    trigger: 'ok',
    preventDefault: () => { preventClose = true },
  }
  emit('ok', bvEvent)
  if (!preventClose) localOpen.value = false
}

const handleCancel = () => {
  let preventClose = false
  const bvEvent = {
    trigger: 'cancel',
    preventDefault: () => { preventClose = true },
  }
  emit('cancel', bvEvent)
  if (!preventClose) localOpen.value = false
}

const onInteractOutside = (e) => {
  if (props.noCloseOnBackdrop) e.preventDefault()
}

const onEscapeKeyDown = (e) => {
  if (props.noCloseOnEsc) e.preventDefault()
}

// These will now work because they modify local state directly
const openModal = () => {
  localOpen.value = true
}

const closeModal = () => {
  localOpen.value = false
}

// ✅ Correct placement, and defineExpose is NOT imported
defineExpose({
  openModal,
  closeModal
});
</script>

<template>
  <!-- Bind to localOpen instead of the computed isOpen -->
  <Dialog :open="localOpen" @update:open="handleOpenChange">
    <!-- ... rest of your template is fine ... -->
    <DialogContent :class="['grid gap-4', maxWidthClass]" @pointer-down-outside="onInteractOutside" @escape-key-down="onEscapeKeyDown">
      <DialogHeader v-if="!hideHeader" class="flex flex-row items-center justify-between space-y-0">
        <div class="flex flex-col gap-1 w-full">
          <DialogTitle class="text-xl">
            <slot name="title">
              {{ title }}
            </slot>
          </DialogTitle>
          <DialogDescription :class="['text-sm text-muted-foreground', description !== 'This is a modal' ? 'block' : 'hidden']">
            <slot name="description">
              {{ description || 'This is the Modal Description' }}
            </slot>
          </DialogDescription>
        </div>
      </DialogHeader>

      <div class="py-2">
        <slot />
      </div>

      <DialogFooter v-if="!hideFooter">
        <slot name="footer" :ok="handleOk" :cancel="handleCancel" :close="handleCancel">
          <Button type="button" :variant="cancelVariant" @click="handleCancel">
            {{ cancelTitle || $t('actions.cancel', 'Cancel') }}
          </Button>
          <Button :disabled="props.okDisabled" type="button" :variant="okVariant" @click="handleOk">
            {{ okTitle || $t('actions.ok', 'OK') }}
          </Button>
        </slot>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
